import { useEffect, useMemo, useState, lazy, Suspense } from "react";
import { useSearchParams, useParams, useNavigate, Link } from "react-router-dom";
import { Search, List, Map } from "lucide-react";
import { api, setPageMeta, setJsonLd, clubListItem } from "@/lib/api";
import { ClubRow } from "@/components/ClubRow";
import { Reveal } from "@/components/Motion";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { MatchedClubs } from "@/components/MatchedClubs";
import { SectionVideo } from "@/components/SectionVideo";

const ClubMap = lazy(() => import("@/components/ClubMap"));

export default function Directory() {
  const [params, setParams] = useSearchParams();
  const { pageNum } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [cities, setCities] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("q") || "");

  const cityOptions = useMemo(
    () => cities.flatMap((c) => c.club_city_slugs).filter((v, i, a) => a.indexOf(v) === i),
    [cities]
  );

  const city = params.get("city") || "";
  const tag = params.get("tag") || "";
  const day = params.get("day") || "";
  const verified = params.get("verified") || "";
  const national = params.get("national") || "";
  const view = params.get("view") === "map" ? "map" : "list";
  const page = pageNum ? Math.max(1, parseInt(pageNum, 10) || 1) : parseInt(params.get("page") || "1", 10);
  const filtered = Boolean(params.get("q") || city || tag || day || verified || national);

  useEffect(() => {
    setPageMeta(
      page > 1
        ? `Run Club Directory — Page ${page} | Ninja Run Club`
        : "Run Clubs Near Me | Find a Local Running Community | Ninja Run Club",
      "Search a national directory of human-verified run clubs by city, meet day, and tags like beginner-friendly and no-drop. Every pace has a place."
    );
  }, [page]);

  useEffect(() => {
    api.get("/cities").then((r) => setCities(r.data)).catch(() => {});
    api.get("/clubs/tags").then((r) => setTags(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const query = {};
    if (params.get("q")) query.q = params.get("q");
    if (city) query.city = city;
    if (tag) query.tag = tag;
    if (day) query.day = day;
    if (verified) query.verified = true;
    if (national) query.national = true;
    query.page = page;
    api.get("/clubs", { params: query }).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [params, city, tag, day, verified, national, page]);

  useEffect(() => {
    if (data.items.length === 0) return;
    return setJsonLd("directory-jsonld", {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "Run Club Directory — Find a Run Club Near You",
      description: `Page ${data.page} of a national directory of ${data.total} human-verified run clubs across America. Community submitted, human reviewed, never invented.`,
      url: `https://ninjarunclub.com/clubs${data.page > 1 ? `/page/${data.page}` : ""}`,
      numberOfItems: data.items.length,
      itemListElement: data.items.map((c, i) => clubListItem(c, (data.page - 1) * 24 + i + 1)),
    });
  }, [data]);

  useEffect(() => {
    const t = setTimeout(() => {
      const current = params.get("q") || "";
      if (q !== current) update("q", q);
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const update = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== "page") next.delete("page");
    if (pageNum) {
      navigate(`/clubs${next.toString() ? `?${next.toString()}` : ""}`, { replace: true });
      return;
    }
    setParams(next, { replace: true });
  };

  const selectCls = "bg-transparent border border-white/20 text-white font-mono-label px-4 py-3 focus:border-white transition-colors duration-200 cursor-pointer";

  return (
    <div className="bg-night min-h-screen">
      <header className="relative overflow-hidden px-5 lg:px-10 pt-10 pb-10 border-b border-white/10">
        <SectionVideo src="/video/run-10.mp4" srcMobile="/video/run-10-mobile.mp4" poster="/video/run-10-poster.jpg" opacity={0.3} testid="directory-hero-video" />
        <div className="relative">
        <Breadcrumbs light items={[["Home", "/"], ["Run Clubs", "/clubs"]]} />
        <Reveal>
          <p className="font-mono-label text-white/40 mt-8">THE NATIONAL DIRECTORY</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-4 leading-none">FIND A RUN CLUB.</h1>
          <p className="mt-4 text-white/50 text-sm max-w-2xl">
            {data.total} run clubs across America and counting — searchable by city, meet day, and tags like beginner-friendly,
            no-drop, and free. Every listing is community submitted and human reviewed before it publishes, and verified clubs
            show a last-checked date. Never invented, ever.
          </p>
        </Reveal>
        </div>
      </header>

      <div data-testid="directory-filters" className="sticky top-16 z-40 bg-night/90 backdrop-blur-xl border-b border-white/10 px-5 lg:px-10 py-4 flex flex-wrap items-center gap-3">
        <div className="flex border border-white/20">
          <button
            data-testid="directory-view-list"
            onClick={() => update("view", "")}
            className={`font-mono-label flex items-center gap-2 px-4 py-3 transition-colors duration-200 ${view === "list" ? "bg-white text-ink" : "text-white/60 hover:text-white"}`}
          >
            <List size={13} /> LIST
          </button>
          <button
            data-testid="directory-view-map"
            onClick={() => update("view", "map")}
            className={`font-mono-label flex items-center gap-2 px-4 py-3 transition-colors duration-200 ${view === "map" ? "bg-white text-ink" : "text-white/60 hover:text-white"}`}
          >
            <Map size={13} /> MAP
          </button>
        </div>
        {view === "map" ? (
          <p className="font-mono-label text-white/40 ml-1">TAP A CITY FOR A PREVIEW. CLUSTERS BREAK APART AS YOU ZOOM.</p>
        ) : (
          <>
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-0 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            data-testid="directory-search-input"
            type="text"
            placeholder="SEARCH CLUB, CITY, NEIGHBORHOOD"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full bg-transparent border-b border-white/20 py-3 pl-6 text-sm placeholder:text-white/30 focus:border-white transition-colors duration-200"
          />
        </div>
        <select data-testid="directory-city-filter" value={city} onChange={(e) => update("city", e.target.value)} className={selectCls}>
          <option value="" className="bg-night">ALL CITIES</option>
          {cityOptions.map((slug) => (
            <option key={slug} value={slug} className="bg-night">{slug.replace(/-/g, " ").toUpperCase()}</option>
          ))}
        </select>
        <select data-testid="directory-tag-filter" value={tag} onChange={(e) => update("tag", e.target.value)} className={selectCls}>
          <option value="" className="bg-night">ALL TAGS</option>
          {tags.map((t) => (
            <option key={t} value={t} className="bg-night">{t.toUpperCase()}</option>
          ))}
        </select>
        <select data-testid="directory-day-filter" value={day} onChange={(e) => update("day", e.target.value)} className={selectCls}>
          <option value="" className="bg-night">ANY DAY</option>
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
            <option key={d} value={d} className="bg-night">{d.toUpperCase()}</option>
          ))}
        </select>
        <button
          data-testid="directory-verified-toggle"
          onClick={() => update("verified", verified ? "" : "1")}
          className={`font-mono-label px-4 py-3 border transition-colors duration-200 ${verified ? "bg-white text-ink border-white" : "border-white/20 text-white/60 hover:border-white"}`}
        >
          VERIFIED
        </button>
        <button
          data-testid="directory-national-toggle"
          onClick={() => update("national", national ? "" : "1")}
          className={`font-mono-label px-4 py-3 border transition-colors duration-200 ${national ? "bg-white text-ink border-white" : "border-white/20 text-white/60 hover:border-white"}`}
        >
          NATIONAL ORGS
        </button>
          </>
        )}
      </div>

      <div className="px-5 lg:px-10 py-8">
        {view === "list" && page === 1 && !filtered && <MatchedClubs />}
        {view === "map" ? (
          <Suspense fallback={<p className="font-mono-label text-white/40 py-24 text-center">PLOTTING THE MILES...</p>}>
            <ClubMap />
          </Suspense>
        ) : loading ? (
          <p data-testid="directory-loading" className="font-mono-label text-white/40 py-16 text-center">LOADING THE MILES...</p>
        ) : data.items.length === 0 ? (
          <div data-testid="directory-empty" className="border border-white/10 p-14 text-center my-10">
            <p className="font-display text-3xl">NO CLUBS MATCH. YET.</p>
            <p className="text-white/50 text-sm mt-3">Loosen the filters, or be the one who puts your city on the map.</p>
            <a href="/submit" className="btn-invert inline-block mt-6 bg-white text-ink font-mono-label px-6 py-3 border border-white hover:bg-transparent hover:text-white">SUBMIT A CLUB</a>
          </div>
        ) : (
          <div data-testid="directory-results">
            <h2 className="sr-only">Run club listings</h2>
            {data.items.map((club, i) => (
              <ClubRow key={club.slug} club={club} index={(data.page - 1) * 24 + i} />
            ))}
          </div>
        )}

        {view === "list" && data.pages > 1 && (
          filtered ? (
            <div className="flex items-center justify-between mt-10" data-testid="directory-pagination">
              <button
                data-testid="directory-prev-page"
                disabled={page <= 1}
                onClick={() => update("page", String(page - 1))}
                className="font-mono-label border border-white/20 px-5 py-3 disabled:opacity-30 hover:border-white transition-colors duration-200"
              >
                PREV
              </button>
              <p className="font-mono-label text-white/40">PAGE {data.page} / {data.pages}</p>
              <button
                data-testid="directory-next-page"
                disabled={page >= data.pages}
                onClick={() => update("page", String(page + 1))}
                className="font-mono-label border border-white/20 px-5 py-3 disabled:opacity-30 hover:border-white transition-colors duration-200"
              >
                NEXT
              </button>
            </div>
          ) : (
            <nav aria-label="Directory pages" className="flex flex-wrap justify-center gap-2 mt-10" data-testid="directory-pagination">
              {Array.from({ length: data.pages }, (_, i) => i + 1).map((n) => (
                <Link
                  key={n}
                  to={n === 1 ? "/clubs" : `/clubs/page/${n}`}
                  data-testid={`directory-page-${n}`}
                  aria-current={n === page ? "page" : undefined}
                  className={`font-mono-label border px-4 py-2.5 transition-colors duration-200 ${
                    n === page ? "bg-white text-ink border-white" : "border-white/20 text-white/60 hover:border-white"
                  }`}
                >
                  {n}
                </Link>
              ))}
            </nav>
          )
        )}

        <div className="border-t border-white/10 mt-14 pt-10 pb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <p className="font-display text-2xl">YOUR CLUB MISSING?</p>
            <p className="text-white/50 text-sm mt-1">Every submission is verified by a human before it goes live.</p>
          </div>
          <a href="/submit" data-testid="directory-submit-cta" className="btn-invert bg-white text-ink font-mono-label px-6 py-3.5 border border-white hover:bg-transparent hover:text-white">
            SUBMIT A CLUB
          </a>
        </div>
      </div>
    </div>
  );
}
